import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ProtectedAdminRoute({ children }) {
  const { currentUser, isLoadingAuth, isAdmin, isModerator, isOwner } = useAuth();
  const [shouldWait, setShouldWait] = useState(true);
  const hasCheckedAuth = useRef(false);

  // Wait for auth to stabilize
  // Problem: isLoadingAuth becomes false BEFORE currentUser is set, creating a race condition
  // Solution: Wait one render cycle after isLoadingAuth becomes false
  useEffect(() => {
    if (!isLoadingAuth && !hasCheckedAuth.current) {
      // Auth just finished loading, wait one render cycle for currentUser to be set
      hasCheckedAuth.current = true;
      setTimeout(() => {
        setShouldWait(false);
      }, 100); // Small delay to let state settle
    } else if (!isLoadingAuth && hasCheckedAuth.current) {
      setShouldWait(false);
    }
  }, [isLoadingAuth, currentUser]);

  // Debug logging (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProtectedAdminRoute]', {
        role: currentUser?.role,
        shouldWait,
        hasAdminAccess: isAdmin || isModerator || isOwner
      });
    }
  }, [currentUser, shouldWait, isAdmin, isModerator, isOwner]);

  // Show loading state while authentication is stabilizing
  if (isLoadingAuth || shouldWait) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0b1220 0%, #1a1d2e 100%)',
        color: '#e2f2ff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(125, 211, 252, 0.2)',
            borderTop: '4px solid #00e6d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Verifying permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login?redirect=/admin" replace />;
  }

  // Check if user has admin/moderator/owner role
  const hasAdminAccess = isAdmin || isModerator || isOwner;

  // Redirect to home with error if insufficient permissions
  if (!hasAdminAccess) {
    return (
      <Navigate
        to="/"
        replace
        state={{
          error: 'Insufficient permissions to access admin area. Contact an administrator if you believe this is a mistake.'
        }}
      />
    );
  }

  // User is authenticated and has admin access
  return children;
}
